import axios, { type InternalAxiosRequestConfig } from "axios";

import {
  clearAuth,
  getAccessExpiresAt,
  getGuestIntakeToken,
  getToken,
} from "@/services/authStorage";
import { refreshAccessToken } from "@/services/refreshTokenService";

const baseURL =
  typeof import.meta.env.VITE_API_URL === "string" &&
  import.meta.env.VITE_API_URL.length > 0
    ? import.meta.env.VITE_API_URL.replace(/\/$/, "")
    : "http://localhost:3001";

export const apiBaseUrl = baseURL;

/**
 * Set while the blocking enrollment modal is mounted (TwoFactorEnrollmentGate).
 *
 * The redirect below is a FULL page load. With the gate already on screen,
 * answering a background 403 by reloading would throw away the six digits
 * someone is halfway through typing into the very form that clears it. The
 * redirect still exists for every other case — a deep link, a tab opened
 * before the gate mounted.
 *
 * SINGLE-WRITER by construction: only TwoFactorEnrollmentGate sets it, only
 * one of those is mounted (AppLayout renders it once), and the browser's
 * event loop gives the interceptor no way to observe a half-written value.
 */
let enrollmentRedirectSuppressed = false;

export function setEnrollmentRedirectSuppressed(value: boolean): void {
  enrollmentRedirectSuppressed = value;
}

export const apiClient = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

apiClient.interceptors.request.use(async (config) => {
  let token = getToken();
  const expiresAt = getAccessExpiresAt();
  const url = typeof config.url === "string" ? config.url : "";
  const isAuthEndpoint =
    url === "/auth/login" ||
    url === "/auth/login-link" ||
    url === "/auth/verify" ||
    url === "/auth/verify-root-invite" ||
    url === "/auth/refresh";

  // Proactive refresh: if we're within 60s of expiry, refresh first
  if (!isAuthEndpoint && token && expiresAt && expiresAt.getTime() < Date.now() + 60_000) {
    try {
      await refreshAccessToken();
      token = getToken();
    } catch {
      // Let the response interceptor handle failures
    }
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    /*
      THE GUEST FALLBACK, and it is deliberately narrow (spec b5b59843).

      An anonymous visitor running the RFQ chat holds an intake-scoped token.
      It is used ONLY for intake calls, mirroring on the client the restriction
      the server already enforces — AuthGuard refuses this token anywhere else,
      so sending it elsewhere would buy nothing and cost a 401 that the
      response interceptor would answer by clearing auth.

      Prefix-matched rather than listed, because the conversation routes are
      `/me/quote-intake/:id` and `/me/quote-intake/:id/messages`; an exact list
      would silently stop covering a route the moment one is added.
    */
    const guestToken = getGuestIntakeToken();
    const isIntakeCall =
      url.startsWith("/me/quote-intake") || url.startsWith("/public/quote-intake");
    if (guestToken && isIntakeCall) {
      config.headers.Authorization = `Bearer ${guestToken}`;
    }
  }
  if (config.data instanceof FormData) {
    delete config.headers["Content-Type"];
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const config = error.config as InternalAxiosRequestConfig & { _retried?: boolean };
    const requestUrl = typeof config?.url === "string" ? config.url : "";

    const isLoginOrLoginLink =
      requestUrl === "/auth/login" || requestUrl === "/auth/login-link";
    const isMagicLinkVerify =
      requestUrl === "/auth/verify" || requestUrl === "/auth/verify-root-invite";
    const isRefresh = requestUrl === "/auth/refresh";
    const isSessionCheck = requestUrl === "/auth/session";
    const isAuthEndpoint = isLoginOrLoginLink || isMagicLinkVerify || isRefresh;

    // A professional who has not configured 2FA is refused on every
    // professional route with this code. THIS is what satisfies "or the next
    // time they access the platform": refresh tokens live 30 days, so
    // professionals hold live sessions right now and handling it only at login
    // would leave them un-enrolled for up to a month (spec 5d9018f9, FM-6).
    //
    // The code is written out rather than imported from totpService, which
    // imports this module — a cycle here would break the client at load time.
    // Kept in sync with TOTP_ENROLLMENT_REQUIRED there.
    //
    // Only the ENROLLMENT code redirects. totp_step_up_required is answered by
    // a dialog where the action was attempted, because bouncing someone off
    // the payout page to confirm a code would lose what they were doing.
    if (status === 403) {
      const code = (error.response?.data as { code?: unknown } | undefined)?.code;
      const alreadyThere = window.location.pathname.startsWith("/security/two-factor");
      if (code === "totp_enrollment_required" && !alreadyThere && !enrollmentRedirectSuppressed) {
        window.location.href = "/security/two-factor";
      }
      return Promise.reject(error);
    }

    if (status !== 401) return Promise.reject(error);

    // Session check 401: the access token is expired or invalid. Do NOT treat
    // this as "logged out" until we've tried the refresh token — a returning
    // visitor whose short-lived (15m) access token lapsed while away (e.g. the
    // full-page dLocal payment redirect) still holds a valid 30-day refresh
    // token. Clearing the session here is exactly what logged users out on
    // return from payment and stranded post-payment resumes on a dead spinner.
    // Try one refresh + retry; only clear auth if the refresh itself fails or
    // the retried session check is still 401. Never redirect — AuthProvider
    // renders the null-user state gracefully.
    if (isSessionCheck) {
      if (config._retried) {
        clearAuth();
        return Promise.reject(error);
      }
      config._retried = true;
      try {
        await refreshAccessToken();
        const token = getToken();
        if (token) {
          config.headers = config.headers ?? {};
          config.headers.Authorization = `Bearer ${token}`;
        }
        return apiClient.request(config);
      } catch {
        clearAuth();
        return Promise.reject(error);
      }
    }

    // Auth endpoints keep their previous behavior
    if (isAuthEndpoint) {
      if (!isLoginOrLoginLink && !isMagicLinkVerify) {
        clearAuth();
      }
      if (!isMagicLinkVerify && !isLoginOrLoginLink) {
        window.location.href = "/login";
      }
      return Promise.reject(error);
    }

    // Non-auth 401: try refresh + retry once
    if (config._retried) {
      clearAuth();
      window.location.href = "/login";
      return Promise.reject(error);
    }

    config._retried = true;
    try {
      await refreshAccessToken();
      const token = getToken();
      if (token) {
        config.headers = config.headers ?? {};
        config.headers.Authorization = `Bearer ${token}`;
      }
      return apiClient.request(config);
    } catch {
      clearAuth();
      window.location.href = "/login";
      return Promise.reject(error);
    }
  },
);
